import { Component, Input, OnInit } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { catchError, finalize, of, take } from 'rxjs';
import {
  CompanyInsightsService,
  formatPhone,
  WorkOrderViewer,
} from '../../../domains/company-insights/company-insights.service';

const RESPONSE_LABELS: Record<NonNullable<WorkOrderViewer['response']>, string> = {
  PENDING: 'No answer yet',
  ACCEPTED: 'Accepted',
  NEGOTIATING: 'Negotiating',
  DECLINED: 'Declined',
};

const RESPONSE_COLORS: Record<NonNullable<WorkOrderViewer['response']>, string> = {
  PENDING: 'medium',
  ACCEPTED: 'success',
  NEGOTIATING: 'warning',
  DECLINED: 'danger',
};

/**
 * Paul, Sprint Review 10/14: tapping "N viewed" on a work order lists who
 * opened it, newest first, how often, and their answer if it went to them.
 * Phone and email show only for installers the company is connected with.
 */
@Component({
  selector: 'gc-work-order-viewers-modal',
  template: `
    <ion-header>
      <ion-toolbar>
        <ion-title>Viewed by</ion-title>
        <ion-buttons slot="end">
          <ion-button (click)="close()" data-cy="work-order-viewers-close">
            <ion-icon slot="icon-only" name="close"></ion-icon>
          </ion-button>
        </ion-buttons>
      </ion-toolbar>
    </ion-header>
    <ion-content>
      <div *ngIf="loading" class="ion-text-center ion-padding">
        <ion-spinner></ion-spinner>
      </div>
      <ion-list *ngIf="!loading && viewers.length" lines="full" data-cy="work-order-viewers">
        <ion-item *ngFor="let viewer of viewers" data-cy="work-order-viewer">
          <ion-label class="ion-text-wrap">
            <h3>{{ viewer.firstName }} {{ viewer.lastName }}</h3>
            <p *ngIf="viewer.businessName">{{ viewer.businessName }}</p>
            <p>
              Viewed {{ viewer.times === 1 ? 'once' : viewer.times + ' times' }}, last on
              {{ viewer.lastSeenOn | date: 'MMM d, h:mm a' }}
            </p>
            <p *ngIf="viewer.phoneNumber || viewer.email" class="contact">
              <a *ngIf="viewer.phoneNumber" href="tel:{{ viewer.phoneNumber }}">{{ phone(viewer.phoneNumber) }}</a>
              <a *ngIf="viewer.email" href="mailto:{{ viewer.email }}">{{ viewer.email }}</a>
            </p>
          </ion-label>
          <ion-note *ngIf="viewer.response" slot="end" [color]="responseColor(viewer)">
            {{ responseLabel(viewer) }}
          </ion-note>
        </ion-item>
      </ion-list>
      <p *ngIf="!loading && !viewers.length" class="ion-padding" data-cy="work-order-viewers-empty">
        {{ failed ? 'The list could not be loaded. Please try again.' : 'Nobody has opened this work order yet.' }}
      </p>
    </ion-content>
  `,
  styles: [
    `
      .contact a {
        margin-right: 12px;
      }
    `,
  ],
})
export class WorkOrderViewersModalComponent implements OnInit {
  @Input() workOrderId!: string;

  viewers: WorkOrderViewer[] = [];
  loading = true;
  failed = false;

  constructor(
    private readonly insights: CompanyInsightsService,
    private readonly modal: ModalController,
  ) {}

  ngOnInit(): void {
    this.insights
      .viewers(this.workOrderId)
      .pipe(
        take(1),
        catchError(() => {
          this.failed = true;
          return of([] as WorkOrderViewer[]);
        }),
        finalize(() => (this.loading = false)),
      )
      .subscribe(viewers => (this.viewers = viewers));
  }

  responseLabel(viewer: WorkOrderViewer): string {
    return viewer.response ? RESPONSE_LABELS[viewer.response] ?? viewer.response : '';
  }

  responseColor(viewer: WorkOrderViewer): string {
    return viewer.response ? RESPONSE_COLORS[viewer.response] ?? 'medium' : 'medium';
  }

  phone(value: string): string {
    return formatPhone(value);
  }

  close(): Promise<boolean> {
    return this.modal.dismiss();
  }
}
